import { ChangeDetectionStrategy, Component, computed, inject, isSignal, ViewEncapsulation } from '@angular/core';
import { IThemeSettings, SettingsMap } from '@partners/settings';
import { ShellStore } from '@partners/state';
import { ThemeAssetComponent } from '@partners/themes';
import { isUndefined } from '@partners/utils';

@Component({
  selector: 'partner-logo',
  standalone: true,
  imports: [ThemeAssetComponent],
  template: `
    @if(hasPrimaryLogo()) {
    <theme-asset [asset]="logoAsset()" source="partner"></theme-asset>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'primary-logo'
  }
})
export class PartnerLogoComponent {
  #shellStore = inject(ShellStore);

  readonly defaultLogoName = 'partner-logo';

  logoSettings = computed<IThemeSettings | undefined>(() => {
    if (!isSignal(this.#shellStore.params$$)) {
      return undefined;
    }

    if (isUndefined(this.#shellStore.params$$())) {
      return undefined;
    }
    const { partner } = this.#shellStore.params$$();
    return SettingsMap.themeSettings(partner);
  });

  /** resolve visibility of the primary logo either default (show) or as defined by logo settings */
  hasPrimaryLogo = computed<boolean>(() => {
    const logoSettings = this.logoSettings();

    /** if no settings are found for this partner's primary logo - we apply the default: visible */
    if (isUndefined(logoSettings?.isPrimaryLogoVisible)) {
      return true;
    }

    const { isPrimaryLogoVisible } = logoSettings;

    return isPrimaryLogoVisible;
  });

  /** dynamically resolve the partner's logo based on the logo settings map - if settings exist */
  logoAsset = computed<string | undefined>(() => {
    const logoSettings = this.logoSettings();

    /** no settings found - resolve the default */
    if (isUndefined(logoSettings)) {
      return this.defaultLogoName;
    }

    const { partner, theme } = this.#shellStore.params$$();
    const processName = this.#shellStore.processId$$();
    const { logoSource, logoSourceMatch } = logoSettings;

    /** no special logo settings found to match the current process - resolve the default */
    if (processName !== logoSourceMatch) {
      return this.defaultLogoName;
    }

    /** no alternative theme found which requires an alternative logo - resolve the default */
    if (theme === partner) {
      return this.defaultLogoName;
    }

    /** if we got here it means terms to resolve an alternative logo have been met, so we render alt logo */
    return `${this.defaultLogoName}-${logoSource}`;
  });
}
